// "Otro" en el rubro del onboarding de marca (ADR-2026-10-12-el-rubro-otro-se-cuenta-y-se-ajusta).
//
// Quien elige "Otro" escribe cuál es su rubro. Muchas veces lo que escribe SÍ es uno de la
// lista —"ropa de bebé", "velas aromáticas", "pastelería"— y eligió "Otro" porque no lo vio o
// no lo reconoció. El rubro de la empresa decide qué modos se le muestran, así que dejarlo en
// "Otro" le esconde los que le sirven: acá se busca a cuál de la lista corresponde.
//
// Por palabras y no con un modelo: la sugerencia se muestra mientras escribe y tiene que ser
// instantánea y gratis. Lo que no encaja queda "Otro" y el texto viaja igual (a la memoria
// del agente y a Brevo, donde el backend ya lo clasifica con un modelo).

export const RUBRO_OTRO = 'Otro';

/** Raíces de palabras por rubro. Se comparan contra el texto sin acentos y en minúscula. */
const PALABRAS: ReadonlyArray<readonly [string, readonly string[]]> = [
  [
    'Moda e Indumentaria',
    [
      'ropa', 'indumentaria', 'moda', 'remera', 'camisa', 'pantalon', 'jean', 'vestido', 'pollera',
      'buzo', 'campera', 'abrigo', 'lenceria', 'malla', 'bikini', 'zapat', 'calzado', 'bota',
      'sandalia', 'cartera', 'bolso', 'mochila', 'accesorio', 'bijou', 'joya', 'joyeria', 'anillo',
      'collar', 'aro', 'reloj', 'lente', 'anteojo', 'gorra', 'sombrero', 'textil', 'tejido',
      'sweater', 'sueter', 'medias', 'pijama', 'uniforme', 'deportiva', 'boutique',
    ],
  ],
  [
    'Hogar y Decoración',
    [
      'hogar', 'deco', 'decoracion', 'mueble', 'muebleria', 'silla', 'mesa', 'sillon', 'sofa', 'cama', 'colchon',
      'almohad', 'sabana', 'blanqueria', 'cortina', 'alfombra', 'lampara', 'iluminacion', 'vela',
      'aromatizador', 'difusor', 'bazar', 'vajilla', 'cocina', 'jardin', 'planta', 'maceta',
      'interiorismo', 'carpinteria', 'ceramica',
    ],
  ],
  [
    'Belleza y Cuidado Personal',
    [
      'belleza', 'cosmetic', 'maquillaje', 'skincare', 'piel', 'crema', 'serum', 'perfum',
      'fragancia', 'pelo', 'cabello', 'peluqueria', 'barberia', 'shampoo', 'unas', 'manicur',
      'estetica', 'spa', 'depilacion', 'cuidado personal',
    ],
  ],
  [
    'Gastronomía',
    [
      'gastronom', 'comida', 'restaurant', 'resto', 'bar', 'cafe', 'cafeteria', 'panaderia',
      'pasteleria', 'reposteria', 'torta', 'alfajor', 'chocolate', 'helado', 'pizza', 'hamburguesa',
      'empanada', 'vino', 'cerveza', 'bebida', 'catering', 'delivery', 'vianda', 'almacen',
      'dietetica', 'alimento', 'snack', 'dulce', 'mermelada', 'fiambre', 'carniceria', 'verduleria',
    ],
  ],
  [
    'Salud y Bienestar',
    [
      'salud', 'bienestar', 'suplement', 'vitamina', 'farmacia', 'gimnasio', 'fitness', 'yoga',
      'pilates', 'nutricion', 'nutricionista', 'kinesio', 'fisioterap', 'psicolog', 'terapia',
      'medic', 'odontolog', 'dentista', 'ortopedia', 'masaje',
    ],
  ],
  [
    'Arte y Manualidades',
    [
      'arte', 'artesania', 'manualidad', 'pintura', 'cuadro', 'ilustracion', 'escultura', 'tejido a mano',
      'crochet', 'bordado', 'macrame', 'resina', 'lamina', 'grabado', 'fotografia artistica',
    ],
  ],
  [
    'Creación de Contenido',
    [
      'contenido', 'influencer', 'creador', 'creadora', 'youtube', 'tiktok', 'streamer', 'podcast',
      'blog', 'community manager', 'redes sociales', 'agencia', 'marketing',
    ],
  ],
  [
    'Tecnología',
    [
      'tecnolog', 'electronica', 'celular', 'telefono', 'computadora', 'notebook', 'gaming',
      'gamer', 'consola', 'auricular', 'parlante', 'software', 'app', 'informatica', 'gadget',
      'cargador', 'smartwatch',
    ],
  ],
  [
    'Librería',
    [
      'libreria', 'libro', 'papeleria', 'cuaderno', 'agenda', 'utiles', 'escolar', 'lapicera',
      'editorial', 'imprenta',
    ],
  ],
  [
    'Juguetería',
    ['jugueteria', 'juguete', 'juego de mesa', 'juegos de mesa', 'peluche', 'muñeco', 'muneco', 'didactico', 'bebe', 'infantil'],
  ],
  [
    'Automotriz',
    [
      'automotor', 'automotriz', 'auto', 'autos', 'moto', 'repuesto', 'neumatico', 'llanta',
      'taller mecanico', 'lubricentro', 'concesionaria', 'accesorios para auto',
    ],
  ],
];

const normalizar = (s: string): string =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9ñ ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * El rubro de la lista al que corresponde lo que escribió, o null si ninguno encaja (o si
 * encajan dos con la misma fuerza: ante la duda, no se ajusta). Una raíz larga cuenta si
 * alguna palabra del texto EMPIEZA con ella ("zapat" → "zapatillas"); una de hasta 4 letras,
 * sólo si ES la palabra o su plural ("bar" no matchea "barbería", "auto" no matchea
 * "autoayuda"); las de varias palabras, si aparecen tal cual.
 */
export function rubroQueCorresponde(texto: string): string | null {
  const t = normalizar(texto);
  if (t.length < 3) return null;
  const palabras = t.split(' ');
  // Los puntos son las PALABRAS del texto que tocan el rubro, no las raíces que matchean: si
  // no, una palabra contaba dos veces ("muebleria" entra por "mueble" y por "muebleria";
  // "muñeco" y "muneco" son la misma raíz sin tilde) y desempataba a favor del rubro que
  // tiene raíces parecidas (review #174).
  const puntos = PALABRAS.map(([rubro, raices]) => {
    const tocadas = new Set<string>();
    for (const raiz of new Set(raices.map(normalizar))) {
      if (raiz.includes(' ')) {
        if (t.includes(raiz)) tocadas.add(raiz);
        continue;
      }
      for (const p of palabras) {
        const matchea =
          raiz.length <= 4 ? p === raiz || p === `${raiz}s` || p === `${raiz}es` : p.startsWith(raiz);
        if (matchea) tocadas.add(p);
      }
    }
    return [rubro, tocadas.size] as const;
  }).sort((a, b) => b[1] - a[1]);
  const [primero, segundo] = puntos;
  if (!primero || primero[1] === 0) return null;
  if (segundo && segundo[1] === primero[1]) return null;
  return primero[0];
}

/**
 * El rubro que se guarda: el elegido, o —si eligió "Otro" y lo que escribió corresponde a uno
 * de la lista— ése.
 */
export function rubroFinal(rubro: string, detalle: string): string {
  if (rubro !== RUBRO_OTRO) return rubro;
  return rubroQueCorresponde(detalle) ?? RUBRO_OTRO;
}
